/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';

// Single home for the retrying-dynamic-import pattern.
// See .claude/context/code-splitting.md for the option semantics.
const Server = () => (<></>);

export const createLazyComponent = (importer, exportName, {
  label = exportName,
  placeholder = null,
  errorBoundaryName = null,
  rethrowOnError = false,
  retries = 3,
} = {}) => {
  const retryingImport = (remaining = retries) => importer().catch((err) => {
    if (remaining > 0) return retryingImport(remaining - 1);
    throw err;
  });

  const LazyComponent = (props) => {
    const moduleRef = useRef(null);
    const [, forceRender] = useState();
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
      let active = true;
      retryingImport()
        .then((module) => {
          if (!active) return;
          moduleRef.current = module[exportName];
          if (!moduleRef.current) {
            throw new Error(`Error importing ${label}.`);
          }
          forceRender(new Date().getTime());
        })
        .catch((err) => { if (active) setLoadError(err); });
      return () => { active = false; };
    }, []);

    if (rethrowOnError && loadError) throw loadError;
    if (typeof window === 'undefined') return Server();
    if (!moduleRef.current) return placeholder ?? Server();

    const element = (<moduleRef.current {...props} />);
    return errorBoundaryName
      ? <ErrorBoundary name={errorBoundaryName}>{element}</ErrorBoundary>
      : element;
  };

  LazyComponent.displayName = `Lazy(${label})`;
  return LazyComponent;
};
